import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Clock, AlertCircle, Play, Lock, Globe } from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { generateId, extractVideoId } from '../utils/helpers';
import Layout from '../components/Layout';

export default function ProjectPage() {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // New Video Form State
    const [videoUrl, setVideoUrl] = useState('');
    const [videoTitle, setVideoTitle] = useState('');
    const [videoVisibility, setVideoVisibility] = useState('public'); // 'public' | 'private'
    const [error, setError] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        const loadProjectData = async () => {
            setLoading(true);
            try {
                // Get Project Info
                const projectRef = doc(db, 'projects', projectId);
                const projectSnap = await getDoc(projectRef);

                if (projectSnap.exists()) {
                    setProject({ id: projectSnap.id, ...projectSnap.data() });

                    // Get Reviews
                    const q = query(collection(db, 'reviews'), where('projectId', '==', projectId));
                    const reviewsSnapshot = await getDocs(q);
                    const reviewsList = [];
                    reviewsSnapshot.forEach((doc) => {
                        reviewsList.push({ id: doc.id, ...doc.data() });
                    });
                    reviewsList.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
                    setReviews(reviewsList);
                } else {
                    navigate('/'); // Project not found
                }
            } catch (error) {
                console.error("Error loading project:", error);
            }
            setLoading(false);
        };

        loadProjectData();
    }, [projectId, navigate]);

    const handleCreateVideo = async (e) => {
        e.preventDefault();
        if (!videoTitle.trim() || !videoUrl.trim()) return;

        const vidId = extractVideoId(videoUrl);
        if (!vidId) {
            setError('Invalid YouTube URL');
            return;
        }

        try {
            const reviewId = generateId('rev');
            const newReview = {
                projectId,
                videoId: vidId,
                videoUrl,
                title: videoTitle.trim(),
                visibility: videoVisibility,
                feedbacks: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'reviews', reviewId), newReview);

            // Update project timestamp
            await updateDoc(doc(db, 'projects', projectId), {
                updatedAt: new Date().toISOString()
            });

            setVideoUrl('');
            setVideoTitle('');
            setVideoVisibility('public');
            setShowCreateModal(false);
            navigate(`/review/${reviewId}`); // Go directly to the new review
        } catch (error) {
            console.error("Error creating review:", error);
            setError('Failed to create video review');
        }
    };

    const handleToggleVisibility = async (review, e) => {
        e.preventDefault(); // Prevent link navigation
        e.stopPropagation();

        const newVisibility = review.visibility === 'private' ? 'public' : 'private';
        const confirmMsg = newVisibility === 'private'
            ? "Lock this video? (Only logged-in users will see it)"
            : "Make this video Public? (Anyone with link can see it)";

        if (!window.confirm(confirmMsg)) return;

        try {
            await updateDoc(doc(db, 'reviews', review.id), {
                visibility: newVisibility,
                updatedAt: new Date().toISOString()
            });

            // Update local state
            setReviews(reviews.map(r =>
                r.id === review.id ? { ...r, visibility: newVisibility } : r
            ));
        } catch (error) {
            console.error("Error updating visibility:", error);
            alert("Failed to update visibility");
        }
    };



    const handleDeleteReview = async (reviewId, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm("Delete this video review?")) return;

        try {
            await deleteDoc(doc(db, 'reviews', reviewId));
            setReviews(reviews.filter(r => r.id !== reviewId));
        } catch (error) {
            console.error("Error deleting review:", error);
        }
    };

    const handleToggleProjectVisibility = async () => {
        if (!project) return;

        const newVisibility = project.visibility === 'private' ? 'public' : 'private';
        const confirmMsg = newVisibility === 'private'
            ? "🔒 LOCK PROJECT?\n\nThis will prevent guests from accessing ANY video in this project, even if they have the link."
            : "🌎 UNLOCK PROJECT?\n\nThis will allow guests with links to view Public videos in this project.";

        if (!window.confirm(confirmMsg)) return;

        try {
            await updateDoc(doc(db, 'projects', projectId), {
                visibility: newVisibility,
                updatedAt: new Date().toISOString()
            });
            setProject({ ...project, visibility: newVisibility });
        } catch (error) {
            console.error("Error updating project visibility:", error);
            alert("Failed to update project visibility");
        }
    };

    if (loading) return <Layout><div className="text-center py-20 text-gray-500">Loading...</div></Layout>;
    if (!project) return null;

    return (
        <Layout projectTitle={project.title} projectId={project.id}>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="flex items-center gap-4 mb-2">
                        <h2 className="text-3xl font-bold text-white">{project.title}</h2>
                        <button
                            onClick={handleToggleProjectVisibility}
                            className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all border ${project.visibility === 'private'
                                    ? 'bg-red-500/20 text-red-400 border-red-500/50 hover:bg-red-500/30'
                                    : 'bg-green-500/20 text-green-400 border-green-500/50 hover:bg-green-500/30'
                                }`}
                            title={project.visibility === 'private' ? "Project is Locked (Private)" : "Project is Open (Public)"}
                        >
                            {project.visibility === 'private' ? <Lock size={14} /> : <Globe size={14} />}
                            {project.visibility === 'private' ? 'LOCKED' : 'PUBLIC'}
                        </button>
                    </div>
                    <p className="text-gray-400">{reviews.length} video reviews</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium transition-colors shadow-lg"
                >
                    <Plus size={18} /> Add Video
                </button>
            </div>

            {reviews.length === 0 ? (
                <div className="text-center py-20 bg-white/5 rounded-2xl border border-dashed border-white/10">
                    <Play size={48} className="mx-auto text-gray-600 mb-4" />
                    <p className="text-xl text-gray-400 mb-4">No videos in this project</p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="text-cyan-400 hover:text-cyan-300 font-medium"
                    >
                        Add your first video
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {reviews.map((review) => (
                        <Link
                            to={`/review/${review.id}`}
                            key={review.id}
                            className="group block bg-[#1a1a1a] rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                        >
                            {/* Thumbnail */}
                            <div className="aspect-video bg-black relative overflow-hidden">
                                <img
                                    src={`https://img.youtube.com/vi/${review.videoId}/mqdefault.jpg`}
                                    alt={review.title}
                                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                                    <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center text-white shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
                                        <Play size={24} className="ml-1" />
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => handleToggleVisibility(review, e)}
                                    className={`absolute top-2 right-2 px-2 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 backdrop-blur-md transition-all border ${review.visibility === 'private'
                                        ? 'bg-red-500/80 text-white border-red-500 hover:bg-red-600'
                                        : 'bg-green-500/80 text-white border-green-500 hover:bg-green-600'
                                        }`}
                                    title="Click to toggle Privacy"
                                >
                                    {review.visibility === 'private' ? <Lock size={12} /> : <Globe size={12} />}
                                    {review.visibility === 'private' ? 'Private' : 'Public'}
                                </button>
                            </div>

                            <div className="p-4">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-white group-hover:text-cyan-400 transition-colors line-clamp-1">
                                        {review.title}
                                    </h3>
                                    <button
                                        onClick={(e) => handleDeleteReview(review.id, e)}
                                        className="text-gray-500 hover:text-red-400 transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>

                                <div className="flex items-center gap-4 text-sm text-gray-400">
                                    <span className="flex items-center gap-1">
                                        <Clock size={12} />
                                        {new Date(review.updatedAt).toLocaleDateString()}
                                    </span>
                                    <span>{review.feedbacks?.length || 0} feedbacks</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {/* Create Video Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-[#1a1a1a] rounded-2xl border border-white/10 w-full max-w-md p-6 shadow-2xl animate-in zoom-in-95 duration-200">
                        <h3 className="text-xl font-bold text-white mb-4">Add New Video</h3>

                        {error && (
                            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-lg mb-4 flex items-center gap-2 text-sm">
                                <AlertCircle size={16} /> {error}
                            </div>
                        )}

                        <form onSubmit={handleCreateVideo}>
                            <div className="space-y-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Video Title</label>
                                    <input
                                        type="text"
                                        autoFocus
                                        value={videoTitle}
                                        onChange={(e) => setVideoTitle(e.target.value)}
                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                                        placeholder="e.g., Draft V1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">YouTube URL</label>
                                    <input
                                        type="text"
                                        value={videoUrl}
                                        onChange={(e) => { setVideoUrl(e.target.value); setError(''); }}
                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                                        placeholder="https://youtube.com/watch?v=..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Visibility</label>
                                    <div className="flex gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setVideoVisibility('public')}
                                            className={`flex-1 py-2 rounded-lg border flex items-center justify-center gap-2 transition-all ${videoVisibility === 'public' ? 'bg-cyan-600/20 border-cyan-500 text-cyan-400' : 'border-white/10 opacity-50 hover:opacity-100'}`}
                                        >
                                            <Globe size={18} /> Public
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setVideoVisibility('private')}
                                            className={`flex-1 py-2 rounded-lg border flex items-center justify-center gap-2 transition-all ${videoVisibility === 'private' ? 'bg-red-600/20 border-red-500 text-red-400' : 'border-white/10 opacity-50 hover:opacity-100'}`}
                                        >
                                            <Lock size={18} /> Private
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        {videoVisibility === 'public' ? 'Anyone with the link can view.' : 'Only logged-in users can view.'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 text-gray-400 hover:text-white font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!videoTitle.trim() || !videoUrl.trim()}
                                    className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Add Video
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
